import { Sparkles, Video } from "lucide-react";
import { useEffect } from "react";
import { Link } from "react-router-dom";

import { Footer } from "@/components/common";
import { Button } from "@/components/ui";
import { ROUTES } from "@/constants/routes";
import { pageViewStorage } from "@/lib/storage/pageView";
import { trackMainPageView } from "@/services/gtm-service/landing/trackMainPageView";
import { useAuthStore } from "@/store/auth.store";

import FinalCta from "./components/FinalCta";
import HowItWorks from "./components/HowItWorks";
import Objections from "./components/Objections";
import SocialProof from "./components/SocialProof";
import UrgencyStrip from "./components/UrgencyStrip";

export function Landing() {
  const { isAuthenticated } = useAuthStore();

  useEffect(() => {
    const previousCount = pageViewStorage.getViewCount(ROUTES.HOME);
    pageViewStorage.incrementViewCount(ROUTES.HOME);
    trackMainPageView({ is_first: previousCount === 0 });
  }, []);

  return (
    <div className="relative overflow-hidden">
      {/* Hero */}
      <section className="relative mx-auto mt-16 max-w-6xl px-4 sm:mt-32 sm:px-6">
        {/* Gradient blobs */}
        <div className="pointer-events-none absolute -top-40 left-1/2 h-[500px] w-[800px] -translate-x-1/2 rounded-full bg-linear-to-br from-brown-200/40 via-brown-300/20 to-transparent blur-3xl" />
        <div className="pointer-events-none absolute top-20 right-0 h-[300px] w-[400px] rounded-full bg-linear-to-bl from-brown-200/30 to-transparent blur-3xl" />

        <div className="relative text-center">
          <div className="mb-7 inline-flex items-center gap-1.5 rounded-full border border-brown-200 bg-white px-3.5 py-1.5 font-semibold text-[12px] text-primary uppercase tracking-wider">
            <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-primary" />
            AI Video · No camera needed
          </div>

          <h1 className="mx-auto flex max-w-4xl flex-col items-center font-extrabold text-3xl text-zinc-900 leading-tight tracking-tight sm:text-5xl lg:text-7xl">
            <span>Post every day.</span>
            <span className="bg-linear-to-r from-brown-600 to-brown-400 bg-clip-text text-transparent">
              Without filming
            </span>
            <span>a single second.</span>
          </h1>

          <p className="mx-auto mt-4 max-w-2xl text-base text-zinc-500 leading-relaxed sm:mt-6 sm:text-xl">
            One photo. One script. Dualio turns you into a talking-head video creator —
            ready to download and share in minutes.
          </p>

          <div className="mt-8 flex flex-col items-center gap-3 sm:mt-10 sm:flex-row sm:justify-center sm:gap-4">
            <Button
              asChild
              size="lg"
              className="h-13 px-8 font-bold text-base sm:text-lg"
            >
              <Link to={isAuthenticated ? ROUTES.CREATE : ROUTES.SIGNUP}>
                <Sparkles className="size-5" />
                Create Your AI Twin
              </Link>
            </Button>
          </div>
        </div>

        {/* Demo preview placeholder */}
        <div className="relative mx-auto mt-12 max-w-2xl">
          <div className="aspect-video overflow-hidden rounded-3xl border border-zinc-200 bg-linear-to-br from-zinc-900 via-zinc-800 to-zinc-900 shadow-2xl">
            <div className="flex h-full items-center justify-center">
              <div className="text-center">
                <div className="mx-auto mb-4 flex h-20 w-20 items-center justify-center rounded-3xl bg-white/10 backdrop-blur-sm">
                  <Video className="h-10 w-10 text-white/60" />
                </div>
                <p className="font-medium text-lg text-white/40">
                  Your AI twin video preview
                </p>
              </div>
            </div>
          </div>
          {/* Glow */}
          <div className="pointer-events-none absolute -inset-4 rounded-[2rem] bg-linear-to-r from-brown-600/20 via-brown-500/10 to-brown-600/20 blur-2xl" />
        </div>
      </section>

      <UrgencyStrip />

      <SocialProof />

      <Objections />

      <HowItWorks />

      <FinalCta />

      <Footer />
    </div>
  );
}
